import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard focus for something drawn over the page (a drawer, a sheet, a dialog): focus moves in when it opens,
 * Tab and Shift+Tab stay inside, Escape closes it (unless a menu inside is open; that menu closes first), and focus
 * goes back to what opened it when it closes. `active` false: only the giving back.
 */
export function useFocusTrap(box: RefObject<HTMLElement | null>, onClose: () => void, active = true): void {
  const opener = useRef<Element | null>(null);

  // remember what had focus when it opened; give it back when it closes
  useEffect(() => {
    opener.current = document.activeElement;
    return () => {
      const el = opener.current as HTMLElement | null;
      if (el && document.contains(el)) el.focus?.();
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    const root = box.current;
    if (root && !root.contains(document.activeElement))
      root.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (!root) return;
      if (e.key === "Escape") {
        // a menu open inside closes first
        if (e.defaultPrevented || root.querySelector('[aria-haspopup][aria-expanded="true"]')) return;
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, box, onClose]);
}
